import { SharedCustomerProduct } from '@bukalapak/openapi-schema/vptrader/product/v1/public/product-domain';
import { ALLOWLIST_FEATURE } from '~/utils/subscription';
import VPNonTraderAPIClient from '~/api/vpnontrader';
import AgenliteAPIClient from '~/api/index';

interface CheckoutList {
  [dealId: string]: string;
}

interface FeatureBenefit {
  title: string;
  subtitle: string;
  detail_link: string;
  detail_text: string;
}

interface Feature {
  feature: string;
  image_url: string;
  subtitle: string;
  benefits: FeatureBenefit[];
}

interface SubscriptionDetail extends Feature {
  skus: any[];
  price: number;
  name: string;
}

interface SubscriptionInfo {
  id: number;
  mitra_id: number;
  package: string;
  renewal_status: string;
  status: string;
  subscription_end_at: string;
  subscription_start_at: string;
  user_id: number;
}

interface SubscriptionFreetrial {
  id: number;
  mitra_id: number;
  user_id: number;
  package: string;
  status: string;
  package_active_duration: number;
  note: string;
  created_at: string;
  updated_at: string;
}

type InitialState = {
  checkoutList: CheckoutList;
  featureList: Feature[];
  subscriptionDetail: SubscriptionDetail;
  recoName: string;
  isBuka20Whitelisted?: boolean;
  buka20MitraSubscriptionProducts: SharedCustomerProduct[];

  loading: {
    checkoutFeatureList: boolean;
    subscriptionDetail: boolean;
    subscriptionInfo: boolean;
    subscriptionFreetrials: boolean;
  };
  subscriptionInfo: SubscriptionInfo;
  subscriptionFreetrials: SubscriptionFreetrial[];
};

const initialState: InitialState = {
  checkoutList: {},
  featureList: [],
  subscriptionDetail: {
    feature: '',
    image_url: '',
    subtitle: '',
    benefits: [],
    skus: [],
    price: 0,
    name: '',
  },
  recoName: '',
  isBuka20Whitelisted: undefined,
  buka20MitraSubscriptionProducts: [],

  loading: {
    checkoutFeatureList: true,
    subscriptionDetail: true,
    subscriptionInfo: true,
    subscriptionFreetrials: true,
  },
  subscriptionFreetrials: [],
  subscriptionInfo: {
    id: 0,
    mitra_id: 0,
    package: '',
    renewal_status: '',
    status: '',
    subscription_end_at: '',
    subscription_start_at: '',
    user_id: 0,
  },
};

const getters = {
  getSubscriptionDetail(state: InitialState) {
    return state.subscriptionDetail;
  },
  getSubscriptionDetailRecoName(state: InitialState) {
    return state.recoName;
  },
  getSubscriptionDealIdByFeature: (state: InitialState) => (feature: string) => {
    if (state.isBuka20Whitelisted) {
      return state.buka20MitraSubscriptionProducts.find(p => p.code === feature)?.id || '';
    }
    return Object.keys(state.checkoutList).find(key => state.checkoutList[key] === feature) || '';
  },
  getSubscriptionLoading(state: InitialState) {
    return state.loading;
  },
  getIsBuka20Whitelisted(state: InitialState) {
    return state.isBuka20Whitelisted;
  },
  getBuka20MitraSubscriptionProducts(state: InitialState) {
    return state.buka20MitraSubscriptionProducts;
  },
  getSubscriptionInfo(state: InitialState) {
    return state.subscriptionInfo;
  },
  getSubscriptionFreetrials(state: InitialState) {
    return state.subscriptionFreetrials;
  },
};

const actions = {
  async retrieveCheckoutFeatureList({ commit, dispatch }) {
    let response;

    commit('setLoadingState', { name: 'checkoutFeatureList', value: true });
    try {
      response = await dispatch('fetchNeoConfigs', ['subscription-feature-list', 'subscription-checkout-list']);
      const featureList = response?.[0]?.data?.data;
      const checkoutList = response?.[1]?.data;

      commit('setCheckoutList', checkoutList);
      commit('setFeatureList', featureList);
    } catch (error) {
      const message = 'Gagal Mendapatkan data.';
      dispatch('catchError', { error, message, noFlashAlert: true });
    } finally {
      commit('setLoadingState', { name: 'checkoutFeatureList', value: false });
    }
    return { response };
  },
  async retrieveMitraPlusSubscriptions({ dispatch }, { packageName }) {
    let response;
    try {
      response = await AgenliteAPIClient.v4.subscription.retrieveMitraPlusSubscriptions(packageName);

      // Check if response has error status
      if (response?.meta?.http_status >= 400) {
        // For 500 errors or other server errors, return null instead of throwing
        if (response.meta.http_status >= 500) {
          console.warn(`Subscription API returned ${response.meta.http_status} for package ${packageName}`);
          return null;
        }
        // For client errors (4xx), throw the error
        throw new Error(`HTTP ${response.meta.http_status}: ${response.meta.message || 'Bad request'}`);
      }
    } catch (error) {
      // If it's a network error or 500+ status, return null instead of throwing
      if (error?.meta?.http_status >= 500 || !error?.meta?.http_status) {
        console.warn(`Subscription API error for package ${packageName}:`, error);
        return null;
      }

      const message = 'Gagal mendapatkan data subscription.';
      dispatch('catchError', { error, message, noFlashAlert: false });
      throw error;
    }
    return response;
  },
  async retrieveSubscriptionDetail({ commit, dispatch }, dealId: number) {
    const whitelistRes = await (AgenliteAPIClient as any).getWallet20WhitelistStatus('mitra-subscription');
    const isBuka20Whitelisted = whitelistRes?.data?.whitelisted;

    commit('setIsBuka20Whitelisted', isBuka20Whitelisted);

    let response;
    if (isBuka20Whitelisted) {
      response = await dispatch('retrieveBuka20SubscriptionDetail', dealId);
    } else {
      response = await dispatch('retrieveLegacySubscriptionDetail', dealId);
    }

    return { response };
  },
  async retrieveBuka20SubscriptionDetail({ state, commit, dispatch }, dealId: number) {
    let response;

    commit('setLoadingState', { name: 'subscriptionDetail', value: true });
    try {
      response = await VPNonTraderAPIClient.getCustomerProducts({ product_type: 'mitra-subscription' });
      commit('setBuka20MitraSubscriptionProducts', response?.customer_products);

      // Guard against undefined elements inside customer_products and return early if no matching product found
      const product = response?.customer_products?.find(p => p?.id === dealId?.toString());
      if (!product) {
        // No matching product; safely exit to prevent TypeError later
        return { response };
      }

      const featureDetail: Feature = state.featureList.find(f => f.feature === product?.code);

      // Convert to legacy data structure. Only used data is filled.
      const sku: any = {
        id: product.id,
        name: product.name,
        price: +product.price,
        ...(product.base_price != null && product.base_price !== ''
          ? { base_price: +product.base_price }
          : {}),
      };

      const discount = +product.discount;
      if (discount) {
        sku.discount = {
          discounted_price: +product.price,
          original_price: +product.base_price,
        };
      }

      const subscriptionDetail: SubscriptionDetail = {
        ...featureDetail,
        skus: [sku],
        price: +product.price,
        name: product.name,
      };

      commit('setSubscriptionDetail', subscriptionDetail);
    } catch (error) {
      const message = 'Gagal Mendapatkan data.';
      dispatch('catchError', { error, message, noFlashAlert: true });
    } finally {
      commit('setLoadingState', { name: 'subscriptionDetail', value: false });
    }
    return { response };
  },
  async retrieveLegacySubscriptionDetail({ commit, dispatch, state }, dealId: number) {
    let response;

    commit('setLoadingState', { name: 'subscriptionDetail', value: true });
    try {
      response = await dispatch('fetchDealsDetail', dealId);
      const dealsDetail = response?.data;

      const feature: string = state.checkoutList[dealId];
      const featureDetail: Feature = state.featureList.find(f => f.feature === feature);

      const subscriptionDetail: SubscriptionDetail = {
        ...featureDetail,
        skus: dealsDetail?.skus,
        price: dealsDetail?.price,
        name: dealsDetail?.name,
      };

      commit('setSubscriptionDetail', subscriptionDetail);
    } catch (error) {
      const message = 'Gagal Mendapatkan data.';
      dispatch('catchError', { error, message, noFlashAlert: true });
    } finally {
      commit('setLoadingState', { name: 'subscriptionDetail', value: false });
    }
    return { response };
  },
  async checkSubscriptionDetailRecommendation({ state, commit, dispatch }) {
    // Reset reconame to not show bottom sheet
    commit('setRecoName', '');

    switch (state.subscriptionDetail?.feature) {
      case ALLOWLIST_FEATURE.REFUND_STANDALONE: {
        const response = await dispatch('retrieveSubscriptionInfo', ALLOWLIST_FEATURE.JAGOAN_VIRTUAL);
        const isActive = response?.data?.status === 'active';

        if (isActive) {
          commit('setRecoName', 'jagoan_downsell');
        }
        break;
      }
      case ALLOWLIST_FEATURE.JAGOAN_VIRTUAL: {
        const response = await dispatch('retrieveSubscriptionInfo', ALLOWLIST_FEATURE.REFUND_STANDALONE);
        const isActive = response?.data?.status === 'active';

        if (isActive) {
          commit('setRecoName', 'jagoan_upsell');
        }
        break;
      }
    }
  },
  async activateSubscriptionFreetrial({ dispatch }, feature: string) {
    let response;
    try {
      dispatch('showAppLoading');
      response = await AgenliteAPIClient.activateSubscriptionFreetrial(feature);
      if (response?.meta?.http_status >= 400) {
        throw new Error(response?.meta?.message || 'Request failed');
      }
    } catch (error) {
      const message =
        (error as any)?.errors?.[0]?.message ||
        (error as any)?.message ||
        'Terjadi kesalahan, silakan coba lagi.';
      dispatch('showFlashAlert', { message, type: 'warning' });
      return null;
    } finally {
      dispatch('hideAppLoading');
    }
    return response;
  },
  async createBuka20MitraSubscriptionTransaction({ dispatch }, payload) {
    let response;
    try {
      dispatch('showAppLoading');
      response = await VPNonTraderAPIClient.createCustomerTransaction(payload);
    } catch (error) {
      const message = error?.errors?.length > 0 ? error.errors[0].message : 'Terjadi kesalahan, silakan coba lagi.';
      dispatch('showFlashAlert', { message, type: 'warning' });
    } finally {
      dispatch('hideAppLoading');
    }
    return response;
  },
  async retrieveFreetrial({ dispatch }) {
    try {
      return await AgenliteAPIClient.retrieveSubscriptionFreetrials();
    } catch (error) {
      const message = 'Gagal Mendapatkan data.';
      dispatch('catchError', { error, message, noFlashAlert: true });
      return null;
    }
  },
  async retrieveSubscriptionInfo({ dispatch, commit }, packageName: string) {
    let response;
    commit('setLoadingState', { name: 'subscriptionInfo', value: true });
    try {
      response = await AgenliteAPIClient.retrieveSubscriptionInfo(packageName);
      commit('setSubscriptionInfo', response?.data);
    } catch (error) {
      const message = 'Gagal Mendapatkan data.';
      dispatch('catchError', { error, message, noFlashAlert: true });
      commit('setSubscriptionInfo', initialState.subscriptionInfo);
    } finally {
      commit('setLoadingState', { name: 'subscriptionInfo', value: false });
    }
    return response;
  },
  async retrieveSubscriptionFreetrials({ dispatch, commit }) {
    let response;
    commit('setLoadingState', { name: 'subscriptionFreetrials', value: true });
    try {
      response = await AgenliteAPIClient.v4.subscription.retrieveSubscriptionFreetrials();
      commit('setSubscriptionFreetrials', response?.data || []);
    } catch (error) {
      const message = 'Gagal Mendapatkan data.';
      dispatch('catchError', { error, message, noFlashAlert: true });
      commit('setSubscriptionFreetrials', []);
    } finally {
      commit('setLoadingState', { name: 'subscriptionFreetrials', value: false });
    }
    return response;
  },
  async updateSubscriptionRenewalStatus({ dispatch, commit }, { packageName, status, reason }) {
    let response;
    try {
      dispatch('showAppLoading');
      response = await AgenliteAPIClient.updateSubscriptionRenewalStatus(packageName, status, reason);
      commit('setSubscriptionInfo', response?.data);
    } catch (error) {
      const message = 'Gagal Mengubah status.';
      dispatch('catchError', { error, message, noFlashAlert: false });
    } finally {
      dispatch('hideAppLoading');
    }
    return response;
  },
};

const mutations = {
  setCheckoutList(state, value: CheckoutList) {
    state.checkoutList = value;
  },
  setFeatureList(state, value: Feature[]) {
    state.featureList = value;
  },
  setSubscriptionDetail(state, value: SubscriptionDetail) {
    state.subscriptionDetail = value;
  },
  setRecoName(state, value: string) {
    state.recoName = value;
  },
  setLoadingState(
    state,
    payload: { name: 'checkoutFeatureList' | 'subscriptionDetail' | 'subscriptionInfo' | 'subscriptionFreetrials'; value: boolean }
  ) {
    state.loading[payload.name] = payload.value;
  },
  setIsBuka20Whitelisted(state, value: boolean) {
    state.isBuka20Whitelisted = value;
  },
  setBuka20MitraSubscriptionProducts(state, value: SharedCustomerProduct[]) {
    state.buka20MitraSubscriptionProducts = value;
  },
  setSubscriptionInfo(state, value: SubscriptionInfo) {
    state.subscriptionInfo = value;
  },
  setSubscriptionFreetrials(state, value: SubscriptionFreetrial[]) {
    state.subscriptionFreetrials = value;
  },
};

export default {
  state: () => ({ ...initialState }),
  mutations,
  getters,
  actions,
};
